import React, { useEffect, useRef, useState } from 'react';
import { COPY } from './copy';
import ConfirmSendModal from './ConfirmSendModal';

const TITLE_ID = 'rai-request-prompt-modal-title';

const Backdrop = ({ children, onClose, labelledBy }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby={labelledBy}
    onClick={onClose}
    style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999,
    }}
  >
    <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 8, padding: 24, maxWidth: 900, width: '90%' }}>
      {children}
    </div>
  </div>
);

const RequestPromptModal = ({ open, onClose, onSubmit, description }) => {
  const [body, setBody] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === 'Escape' && !submitting && !confirmOpen) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, submitting, confirmOpen, onClose]);

  if (!open) return null;

  const handleClose = () => {
    setBody('');
    setError(null);
    setConfirmOpen(false);
    onClose();
  };

  const handleSendClick = () => {
    if (!body.trim()) {
      setError('Please describe the information you need.');
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ body: body.trim() });
      handleClose();
    } catch (err) {
      const msg = err.response?.data?.errors?.join(', ') || 'Failed to send request';
      setError(msg);
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Backdrop onClose={handleClose} labelledBy={TITLE_ID}>
        <h2 id={TITLE_ID} className="text-lg font-semibold mb-4">{COPY.modal.title}</h2>
        <p className="mb-4 text-sm text-slate-600">{description || COPY.modal.description}</p>
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          style={{ width: '100%', minHeight: 160, padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
          aria-label={COPY.modal.title}
        />
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        <div className="flex flex-row gap-x-3 justify-end mt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="btn btn-ghost"
          >
            {COPY.buttons.cancel}
          </button>
          <button
            type="button"
            onClick={handleSendClick}
            disabled={submitting}
            className="btn btn-primary"
          >
            {COPY.buttons.send}
          </button>
        </div>
      </Backdrop>
      <ConfirmSendModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        submitting={submitting}
      />
    </>
  );
};

export default RequestPromptModal;
